// packages/shared/2fa/useTwoFactorSetup.js
// Cross-platform 2FA setup logic (fetch QR, verify code, expose recovery codes)

import { useEffect, useState, useRef } from 'react';

import api from '../../config/axios';

export function useTwoFactorSetup() {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [otpAuthUrl, setOtpAuthUrl] = useState(null);
  const [secretBase32, setSecretBase32] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setError('');
      try {
        const res = await api.post('/twofa/setup', {});
        setQrDataUrl(res?.data?.qrDataUrl ?? null);
        setOtpAuthUrl(res?.data?.otpauth ?? null);
        setSecretBase32(res?.data?.secret ?? null);
        // optional: setDevSecret(res.data.devSecretBase32);
      } catch (e) {
        setError('Could not start 2FA setup.');
      }
    })();
  }, []);

  const canSubmit = code && code.length === 6;

  const verify = async () => {
    if (!code || code.trim().length !== 6) return false;
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/twofa/verify', { code: code.trim() });
      const codes = Array.isArray(res?.data?.recoveryCodes)
        ? res.data.recoveryCodes
        : [];
      setRecoveryCodes(codes);
      setLoading(false);

      return {
        fresh: true,
        codes: codes,
      };
    } catch (e) {
      // Never assume e.response exists (native fetch can throw plain Error)
      const serverMsg = e?.response?.data?.error;
      setError(serverMsg === 'invalid_code' ? 'invalid_code' : 'verify_failed');
      setLoading(false);
      return false;
    }
  };

  return {
    // state
    qrDataUrl,
    otpAuthUrl,
    secretBase32,
    code,
    setCode,
    recoveryCodes,
    loading,
    error,
    canSubmit,
    // actions
    verify,
  };
}

export default useTwoFactorSetup;
